import { motion } from 'motion/react';
import { useId } from 'react';

import { cn } from '@/shared/lib/cn';

interface Segment<T extends string> {
  value: T;
  label: string;
  /** Счётчик рядом с подписью — например, число невыполненных задач. */
  count?: number;
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Переключатель режимов внутри экрана (День / Неделя, Цели / Привычки).
 *
 * Активный сегмент подсвечивается «пилюлей», которая плавно переезжает между
 * позициями через общий layout-анимации motion — за счёт этого переключение
 * читается как одно движение, а не как мигание двух состояний.
 */
export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  const layoutId = useId();

  return (
    <div
      role="tablist"
      className={cn('flex gap-1 rounded-full bg-surface-2 p-1', className)}
    >
      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(segment.value)}
            className={cn(
              'relative flex-1 rounded-full px-3 py-2 text-[13px] font-medium',
              'transition-colors duration-200',
              active ? 'text-brand-contrast' : 'text-muted hover:text-content',
            )}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-full bg-brand"
                transition={{ type: 'spring', damping: 30, stiffness: 380 }}
              />
            )}
            <span className="relative flex items-center justify-center gap-1.5">
              {segment.label}
              {segment.count !== undefined && segment.count > 0 && (
                <span
                  className={cn(
                    'rounded-full px-1.5 text-[11px] tabular-nums',
                    active ? 'bg-white/20' : 'bg-surface-3',
                  )}
                >
                  {segment.count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
